import { useMutation, useQuery } from '@tanstack/react-query'
import { baseAPICall } from '../../services/baseAPICall'
import { clientInstance } from '../../utils/http-client'
import { queryClient } from '../../services/queryClient'
import {
  ChainSearchResult,
  ImportData,
  ImportPreview,
  ImportType,
} from './importer-types'

/* -------------
List Import Previews
-------------- */
export async function getImportPreviews(signal?: AbortSignal) {
  return baseAPICall<ImportPreview[]>(() =>
    clientInstance.get('importengine/previews/', { signal })
  )
}

export const useGetImportPreviews = (signal?: AbortSignal, disabled = false) =>
  useQuery({
    queryKey: ['import-previews'],
    queryFn: () => getImportPreviews(signal),
    enabled: !disabled,
  })

/* -------------
Create Import Preview
-------------- */
type CreateImportPreviewParams = {
  type: ImportType
  import_data: {
    data: ImportData
    raw_data?: string
  }
}

export async function createImportPreview(values: CreateImportPreviewParams) {
  return baseAPICall<ImportPreview>(
    () => clientInstance.post('importengine/previews/', values),
    { successMessage: 'Import preview created successfully' }
  )
}

/* -------------
Get Import Preview
-------------- */
async function getImportPreview(id: number | null, signal?: AbortSignal) {
  if (!id) return null
  return baseAPICall<ImportPreview>(() =>
    clientInstance.get(`importengine/previews/${id}/`, { signal })
  )
}

export const useGetImportPreview = (id: number | null, signal?: AbortSignal) =>
  useQuery({
    queryKey: ['import-preview', id],
    queryFn: () => getImportPreview(id, signal),
    enabled: !!id,
  })

/* -------------
Update Import Preview
-------------- */
type UpdateImportPreviewParams = CreateImportPreviewParams

export async function updateImportPreview(
  id: number,
  values: Partial<UpdateImportPreviewParams>
) {
  return baseAPICall<ImportPreview>(
    () => clientInstance.patch(`importengine/previews/${id}/`, values),
    { successMessage: 'Import preview updated successfully' }
  )
}

/* -------------
Delete Import Preview
-------------- */
async function deleteImportPreview(id: number) {
  return baseAPICall(
    () => clientInstance.delete(`importengine/previews/${id}/`),
    { successMessage: 'Import preview deleted successfully' }
  )
}

export const useDeleteImportPreview = () => {
  return useMutation({
    mutationFn: deleteImportPreview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-previews'] })
    },
  })
}

/* -------------
Search Chains
-------------- */
async function searchChains(query: string, signal?: AbortSignal) {
  return baseAPICall<ChainSearchResult[]>(() =>
    clientInstance.get('importengine/chains/search/', {
      signal,
      params: { q: query },
    })
  )
}

export const useSearchChains = (query: string, signal?: AbortSignal) =>
  useQuery({
    queryKey: ['chains-search', query],
    queryFn: () => searchChains(query, signal),
    enabled: !!query,
  })

/* -------------
Perform Match
-------------- */
export async function performMatch(id: number) {
  return baseAPICall(() =>
    clientInstance.post(`importengine/previews/${id}/match/`)
  )
}

/* -------------
Import Data
-------------- */
export async function performImport(id: number) {
  return baseAPICall(() =>
    clientInstance.post(`importengine/previews/${id}/import/`)
  )
}
